// ── State-update helpers for realtime events ──

import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import {
  IncidentHeadcountCounts,
  RosterPersonStatusChangedEvent,
} from './incidentRealtimeTypes';
import { normalizeApiPersonStatus } from '../Utils/headcountStatus';

/** Valid headcount statuses — single source of truth. */
export const HEADCOUNT_STATUSES = {
  NOT_CHECKED: 'notChecked',
  ACCOUNTED: 'accounted',
  NOT_ACCOUNTED: 'notAccounted',
} as const;

const VALID_STATUSES = new Set<string>([
  HEADCOUNT_STATUSES.NOT_CHECKED,
  HEADCOUNT_STATUSES.ACCOUNTED,
  HEADCOUNT_STATUSES.NOT_ACCOUNTED,
]);

/** Returns true if the value is a valid headcount status (string or numeric). */
export const isValidHeadcountStatus = (status: unknown): boolean => {
  if (typeof status === 'string' && VALID_STATUSES.has(status)) return true;
  // API may send numeric enum values: 0=NotChecked, 1=Accounted, 2=NotAccounted
  if (typeof status === 'number' && (status === 0 || status === 1 || status === 2)) return true;
  return false;
};

/** Normalize a raw status (string or numeric) into a PersonStatus string. */
export const normalizeHeadcountStatus = (status: unknown): PersonStatus => {
  return normalizeApiPersonStatus(status);
};

/**
 * Apply a RosterPersonStatusChanged event to a roster array.
 * Returns a new array (immutable update). If no matching person is found, the array is returned unchanged.
 */
export const applyRosterPersonStatusChanged = (
  roster: IncidentRosterPerson[],
  event: RosterPersonStatusChangedEvent,
): IncidentRosterPerson[] => {
  if (!isValidHeadcountStatus(event.effectiveHeadcountStatus)) {
    console.warn('[realtime] Invalid effectiveHeadcountStatus in event, ignoring:', event.effectiveHeadcountStatus);
    return roster;
  }

  const normalizedStatus = normalizeHeadcountStatus(event.effectiveHeadcountStatus);

  let found = false;
  const updated = roster.map((person) => {
    if (person.personId !== event.personId) return person;
    found = true;
    return {
      ...person,
      status: normalizedStatus,
      lastUpdatedAtUtc: event.lastUpdatedAtUtc,
      lastUpdatedByName: event.lastUpdatedByDisplayName ?? person.lastUpdatedByName,
    };
  });

  if (!found) {
    console.debug('[realtime] RosterPersonStatusChanged for unknown personId:', event.personId);
  }

  return updated;
};

/**
 * Apply realtime counts to replace current counts.
 * Returns the event counts directly (authoritative, no local increments).
 */
export const applyRealtimeCounts = (
  _currentCounts: IncidentHeadcountCounts,
  eventCounts: IncidentHeadcountCounts,
): IncidentHeadcountCounts => {
  return { ...eventCounts };
};

/**
 * Return a human-readable label for a connection state.
 */
export const getRealtimeConnectionStateLabel = (
  state: string,
): string => {
  switch (state) {
    case 'connecting': return 'Connecting…';
    case 'connected': return 'Connected';
    case 'reconnecting': return 'Reconnecting…';
    case 'disconnected': return 'Disconnected';
    case 'error': return 'Connection error';
    default: return state;
  }
};
